import tastify from '@frontastic/catwalk/src/js/helper/tastify';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import JournalCategorySchema from '../../../../components/molecules/journal/seo/category/JournalCategorySchema';
import JournalArticle from '../../../../components/templates/journal/journalArticle';

import { Tastic } from '@frontastic/catwalk/src/js/types/frontend';
import { JournalArticleProps } from '@srcDS/js/components/templates/journal/journalArticle/JournalArticle';

interface Category {
  slug: string;
  title: string;
  url: string;
}

interface Author {
  firstName: string;
  lastName: string;
}

interface MediaDevice {
  url: string;
  title: string;
  type: string;
  contentType: string;
  width: number;
  height: number;
}

interface Thumbnail {
  showControls: boolean | null;
  muted: boolean | null;
  desktopCloudinary: string;
  mobileCloudinary: string;
  tabletCloudinary: string;
  desktop: MediaDevice;
  mobile: MediaDevice | null;
  tablet: MediaDevice | null;
}

interface RelatedArticlesCollection {
  count: number;
  total: number;
  skip: number;
  limit: number;
  items: Array<{
    objectID: string;
    id: string;
    type: string;
    title: string;
    slug: string;
    url: string;
    shortDescription: string;
    isPinnedWebsiteHome: boolean;
    isPinnedBlogHome: boolean;
    isPinnedCategory: boolean;
    publishDate: string;
    publishedAt: string;
    website: string;
    shareButtons: string[];
    author: Author | null;
    thumbnail: Thumbnail;
    categories: Category[];
  }>;
}

export interface JournalArticleTasticProps {
  isServerSideRendering: boolean;
  tastic: Tastic;
  data: {
    title: string;
    blogArticleData: {
      article: JournalArticleProps & {
        [key: string]: any;
      };
      topCategories: {
        slug: string;
        title: string;
        url: string;
      }[];
      relatedProducts;
      relatedArticlesCollection;
      products;
    };
    articleFirstTag: {
      title: string;
      name: string;
      slug: string;
      url: string;
    };
    articlesSliderTitle: string;
    productSliderTitle: string;
    relatedTagsTitle: string;
  };
  route;
  context;
  node;
}

const JournalArticleTastic = ({
  isServerSideRendering,
  tastic,
  data,
  route,
  context,
  node,
}: JournalArticleTasticProps) => {
  const triggerRoute = route?.parameters?.slug;
  const locale = context?.locale === 'en_AU@AUD' ? 'AU' : 'NZ';

  if (!data[`show${locale}`]) return null;

  const isSSR = typeof isServerSideRendering === 'boolean' && isServerSideRendering;
  const content = data?.blogArticleData;
  const articleData = content?.article;
  const categories = content?.topCategories ?? [];

  const relatedArticles = content?.relatedArticlesCollection?.items || [];
  const relatedProducts = content?.relatedProducts?.items || [];
  const currentCategory = articleData.categories?.[0];
  const pageTitle = articleData.title ?? '';
  const description = articleData.shortDescription ?? '';
  const metaData = {
    title: articleData?.metaTitle || pageTitle,
    description: articleData?.metaDescription || description,
    keywords: articleData?.metaKeywords || '',
  };
  const socialThumbnail = articleData.socialShareImage ?? articleData?.thumbnail?.desktop;
  const navigationProps = {
    currentCategory,
    categories: categories,
    title: pageTitle,
    pageTitle: data?.title,
    hideNavigation: articleData.website?.title === 'Jag',
  };

  useEffect(() => {
    if (!isSSR) {
      const body = document?.getElementById('bodyWrapper');
      body && body.scrollTo(0, 0);
    }
  }, [triggerRoute]);

  return (
    <>
      <Helmet>
        <link href="https://unpkg.com/cloudinary-video-player@1.9.5/dist/cld-video-player.min.css" rel="stylesheet" />
        <script
          src="https://unpkg.com/cloudinary-video-player@1.9.5/dist/cld-video-player.min.js"
          type="text/javascript"
        />
      </Helmet>
      <JournalArticle
        isSSR={isSSR}
        banner={articleData.banner}
        articleSections={articleData.content}
        currentCategory={currentCategory}
        author={articleData.author}
        title={pageTitle}
        publishedDate={articleData.publishDate}
        description={articleData.shortDescription}
        relatedTags={articleData.tags || []}
        thumbnail={articleData.thumbnail?.desktop?.url}
        relatedArticles={relatedArticles}
        articlesSliderTitle={data.articlesSliderTitle}
        relatedProducts={relatedProducts}
        productSliderTitle={data.productSliderTitle}
        relatedTagsTitle={data.relatedTagsTitle}
        navigationProps={navigationProps}
        shareButtons={articleData?.shareButtons}
        triggerRoute={triggerRoute}
      />
      <JournalCategorySchema
        title={metaData?.title}
        description={metaData?.description}
        keywords={metaData?.keywords}
        url={null}
        image={socialThumbnail}
        article={articleData}
        placement="Article"
        showCanonicalLink={node?.configuration?.showCanonicalLink}
      />
    </>
  );
};

export default tastify({
  translate: true,
  connect: {
    tastic: true,
    isServerSideRendering: true,
    route: true,
    context: true,
  },
})(JournalArticleTastic);
